import React, { useEffect, useState } from 'react';
import { App, Modal, Input, Table, Typography } from 'antd';
import { HtsInputType, MAX_ANSWER_MESSAGE_LENGTH } from '@evaltick/shared';
import type { HtsGroupField } from '@evaltick/shared';
import { Button } from '@/components/Button';
import type { HtsAdminFollowUpItem } from '@/services/api';
import styles from './AdminFollowUpsModal.module.scss';

interface AdminFollowUpsModalProps {
  open: boolean;
  items: HtsAdminFollowUpItem[];
  /** Reply drafts by questionId, owned by Sessions so they survive closing the modal. */
  drafts: Record<string, string>;
  onDraftChange: (questionId: string, value: string) => void;
  /** The confirm dialog's Submit button spins until this resolves. */
  onSubmit: () => Promise<void>;
  onClose: () => void;
}

type GroupRow = Record<string, string>;

const parseGroupRows = (value: HtsAdminFollowUpItem['answerValue']): GroupRow[] => {
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((r): r is GroupRow => typeof r === 'object' && r !== null) : [];
  } catch {
    return [];
  }
};

const formatGroupCell = (field: HtsGroupField, raw: string | undefined): string => {
  if (!raw) return '—';
  if (field.inputType !== HtsInputType.CHECKBOXES) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed.join(', ') : '—';
  } catch {
    return raw;
  }
};

const YourAnswer: React.FC<{ item: HtsAdminFollowUpItem }> = ({ item }) => {
  if (item.groupFields && item.groupFields.length > 0) {
    const rows = parseGroupRows(item.answerValue);
    if (rows.length === 0) return <p className={styles.contextText}>None</p>;
    return (
      <Table
        size="small"
        pagination={false}
        dataSource={rows}
        rowKey={(_, i) => i ?? 0}
        scroll={{ x: 'max-content' }}
        columns={item.groupFields.map((field) => ({
          title: field.label,
          dataIndex: field.key,
          key: field.key,
          render: (raw: string | undefined) => formatGroupCell(field, raw),
        }))}
      />
    );
  }
  const text = Array.isArray(item.answerValue)
    ? item.answerValue.length === 0 ? 'None' : item.answerValue.join(', ')
    : item.answerValue === '[]' || !item.answerValue ? 'None' : item.answerValue;
  return <p className={styles.contextText}>{text}</p>;
};

/**
 * The guest's modal for answering an admin's follow-up questions: one per step, in any order, all
 * editable until Submit, which asks for confirmation in a dialog stacked over this one.
 */
export const AdminFollowUpsModal: React.FC<AdminFollowUpsModalProps> = ({
  open,
  items,
  drafts,
  onDraftChange,
  onSubmit,
  onClose,
}) => {
  const [step, setStep] = useState(0);
  const { modal } = App.useApp();

  // Stays mounted to animate closed; each opening starts on the first question.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const answeredCount = items.filter((i) => (drafts[i.questionId] ?? '').trim().length > 0).length;
  const allAnswered = answeredCount === items.length;
  const isLast = step === items.length - 1;
  const item = items[step];

  const confirmSubmit = () =>
    modal.confirm({
      title: 'Submit your answers?',
      content: "Once submitted, they can't be changed.",
      icon: null,
      centered: true,
      okText: 'Submit',
      cancelText: 'Review answers',
      maskClosable: true,
      onOk: onSubmit,
    });

  if (!item) return null;

  const footer = (
    <div className={styles.actions}>
      {step > 0 && (
        <Button appearance="outline" onClick={() => setStep(step - 1)}>
          Back
        </Button>
      )}
      {isLast ? (
        <>
          {!allAnswered && (
            <Typography.Text type="secondary" className={styles.answeredCount}>
              {answeredCount} of {items.length} answered
            </Typography.Text>
          )}
          <Button appearance="solid" disabled={!allAnswered} onClick={confirmSubmit}>
            Submit
          </Button>
        </>
      ) : (
        <Button appearance="solid" onClick={() => setStep(step + 1)}>
          Next
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      destroyOnHidden
      centered
      width={640}
      title="Follow-up questions about your HTS answers"
      footer={footer}
      onCancel={onClose}
    >
      <div className={styles.eyebrowRow}>
        <span className={styles.eyebrow}>About question {item.questionNumber}</span>
        <span className={styles.progress}>
          {step + 1} of {items.length}
        </span>
      </div>

      <div className={styles.followUpContext}>
        <div className={styles.contextScroll}>
          <p className={styles.contextQuestion}>&ldquo;{item.questionText}&rdquo;</p>
          <span className={styles.contextLabel}>Your answer</span>
          <YourAnswer item={item} />
          {item.followUpHistory.length > 0 && (
            <>
              <span className={styles.contextLabel}>Earlier follow-ups</span>
              {item.followUpHistory.map((exchange, i) => (
                <div key={i} className={styles.exchange}>
                  <p className={styles.contextText}>
                    <strong>Q:</strong> {exchange.question}
                  </p>
                  {exchange.answer && (
                    <p className={styles.contextText}>
                      <strong>A:</strong> {exchange.answer}
                    </p>
                  )}
                </div>
              ))}
            </>
          )}
          <hr className={styles.divider} />
          <p className={styles.adminQuestion}>{item.followUpQuestion}</p>
        </div>
      </div>

      <span className={styles.contextLabel}>Your answer</span>
      <Input.TextArea
        className={styles.textarea}
        autoSize={{ minRows: 4, maxRows: 10 }}
        maxLength={MAX_ANSWER_MESSAGE_LENGTH}
        value={drafts[item.questionId] ?? ''}
        onChange={(e) => onDraftChange(item.questionId, e.target.value)}
        placeholder="Type your answer…"
      />
    </Modal>
  );
};

export default AdminFollowUpsModal;
